import { Configuration, LogLevel, type AccountInfo, type IPublicClientApplication } from '@azure/msal-browser';
import { config } from './config';

export const msalConfig: Configuration = {
    auth: {
        clientId: config.azureClientId,
        authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
        // Résolu à l'exécution : par défaut l'origine réellement servie, de sorte
        // qu'un déploiement sur un autre domaine ne renvoie plus vers la prod.
        redirectUri: config.azureRedirectUri || window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        break;
                    case LogLevel.Warning:
                        console.warn(message);
                        break;
                    case LogLevel.Info:
                        console.info(message);
                        break;
                    case LogLevel.Verbose:
                        console.debug(message);
                        break;
                }
            },
            logLevel: LogLevel.Warning,
        },
    },
};

export const loginRequest = {
    scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/** Marge avant expiration en deçà de laquelle l'id_token est renouvelé, en secondes. */
const MARGE_ID_TOKEN_SECONDES = 5 * 60;

/**
 * id_token à présenter à l'API, renouvelé s'il expire bientôt.
 *
 * Le cache de MSAL peut rendre un id_token proche de l'expiration, voire expiré, tant
 * que le jeton d'accès reste valable. L'API refuserait alors la connexion, et le repli
 * sur la fenêtre de connexion recommencerait sans fin avec le même jeton.
 */
export async function freshIdToken(instance: IPublicClientApplication, account: AccountInfo): Promise<string> {
    const request = { ...loginRequest, account };
    let response = await instance.acquireTokenSilent(request);
    const exp = (response.idTokenClaims as { exp?: number } | undefined)?.exp ?? 0;
    if (exp - Date.now() / 1000 < MARGE_ID_TOKEN_SECONDES) {
        response = await instance.acquireTokenSilent({ ...request, forceRefresh: true });
    }
    return response.idToken;
}

export const apiScopes = {
    scopes: [`api://${config.azureClientId}/access_as_user`],
};
